import React from "react";
import { useHistory } from "react-router-dom";
import { Card, Segment, Statistic } from "semantic-ui-react";
import { StateKey } from "../models/globalTypes";
import { Organization } from "../models/Organization";
import { PermitRegister } from "../models/PermitRegister";
import { StateKeyList } from "../models/StateKey";

interface Props {
  data: { stateKey?: string; count: number }[];
  currentPermitRegister?: PermitRegister;
  currentOrganization?: Organization;
  /** When provided, the active drill-down state to highlight. */
  selectedState?: StateKey | null;
  /**
   * When provided, the tiles switch to selection mode: clicking toggles the
   * dashboard drill-down state instead of navigating to the permits list.
   */
  onSelectState?: (state: StateKey) => void;
}

const RegisterPermitsCounts = ({
  data,
  currentOrganization,
  currentPermitRegister,
  selectedState,
  onSelectState,
}: Props) => {
  var history = useHistory();

  const requestedPermits = data.filter((s) => s.stateKey === StateKeyList.REQUESTED.key)[0]?.count ?? 0;
  const livePermits = data.filter((s) => s.stateKey === StateKeyList.ISSUED.key)[0]?.count ?? 0;
  const expiredPermits = data.filter((s) => s.stateKey === StateKeyList.EXPIRED.key)[0]?.count ?? 0;
  const inprogressPermits = data.filter((s) => s.stateKey === StateKeyList.IN_PROGRESS.key)[0]?.count ?? 0;
  const completedPermits = data.filter((s) => s.stateKey === StateKeyList.COMPLETED.key)[0]?.count ?? 0;
  const readyToIssuePermits = data.filter((s) => s.stateKey === StateKeyList.READY_TO_ISSUE.key)[0]?.count ?? 0;
  //const totalPermits = data.reduce((sum, item) => sum + item.count, 0);

  // Dashboard selection mode toggles drill-down state; register context navigates.
  const handleStateClick = (state: StateKey) => {
    if (onSelectState) {
      onSelectState(state);
      return;
    }

    const queryString = new URLSearchParams();
    queryString.set("state", state.toString());

    history.push({
      pathname: `/${currentOrganization!.name}/projects/${currentPermitRegister!.project!.name}/registers/${currentPermitRegister!.title}/permits`,
      search: queryString.toString(),
    });
  };

  const renderStatistic = (value: number, label: string, state: StateKey) => {
    const isSelected = !!onSelectState && selectedState === state;
    return (
      <Card
        as="a"
        onClick={() => handleStateClick(state)}
        color={isSelected ? "green" : undefined}
        raised={isSelected}
        style={isSelected ? { boxShadow: "0 0 0 2px #21ba45 inset" } : undefined}
      >
        <Segment basic textAlign="center">
          <Statistic color={isSelected ? "green" : undefined}>
            <Statistic.Label>{label}</Statistic.Label>
            <Statistic.Value>{value}</Statistic.Value>
          </Statistic>
        </Segment>
      </Card>
    );
  };

  return (
    <>
      <Segment basic>
        <Card.Group stackable doubling itemsPerRow={3}>
          {renderStatistic(requestedPermits, "Requested/Pending Approval", StateKeyList.REQUESTED.key)}
          {renderStatistic(readyToIssuePermits, "Ready to Issue Permits", StateKeyList.READY_TO_ISSUE.key)}
          {renderStatistic(livePermits, "Live/Issued Permits", StateKeyList.ISSUED.key)}
          {renderStatistic(expiredPermits, "Expired Permits", StateKeyList.EXPIRED.key)}
          {renderStatistic(inprogressPermits, "In progress Permits", StateKeyList.IN_PROGRESS.key)}
          {renderStatistic(completedPermits, "Completed Permits", StateKeyList.COMPLETED.key)}
        </Card.Group>
      </Segment>
    </>
  );
};

export default RegisterPermitsCounts;
